
<template>
  <SearchBar
    :search-results="searchIndices.collection.results"
    :should-collapse-menu="shouldCollapseMenu"
    @update-search-term="updateSearchTerm"
    @update-should-collapse-menu="shouldCollapseMenu = false"
  />
</template>

<script>
// Package imports
import Fuse from 'fuse.js';
import {mapState} from 'vuex';
import {featureOptions} from '@/services/OverlaysService';

// Services and Helpers
import {getCollection} from '@/lib/filters/filters';

// Components
import SearchBar from '@/components/SearchBar/SearchBar.vue';

/** @typedef {import('@/services/OverlaysService/index').FeatureOption} FeatureOption */

export default {
  name: 'LocalSearchBar',
  components: {
    SearchBar,
  },
  data() {
    return {
      searchIndices: {
        collection: {
          ref: 'collectionResults',
          results: [],
          numResults: 5,
        },
      },
      searchTerm: null,
      shouldDisplayResults: true,
      loading: false,
      shouldCollapseMenu: false,
    };
  },
  computed: {
    ...mapState(['isEditableMap']),
  },
  methods: {
    /**
     * @param {string} collectionName
     */
    async renderCollectionResults(collectionName) {
      // For testing search by collection, temporarily getting industrial buildings
      const source = getCollection(collectionName).getSource();
      // Fuse is a fuzzy search library on arrays of objects
      let fuse;
      if (this.isEditableMap) {
        fuse = new Fuse(source, {keys: ['name']});
      } else {
        const visibleFeatures = source.filter((feature) => feature.visible);
        fuse = new Fuse(visibleFeatures, {keys: ['name']});
      }
      // Fuzzy search by search term results
      const numResults = this.searchIndices.collection.numResults;
      const output = fuse.search(this.searchTerm).splice(0, numResults);

      for (const [index, result] of output.entries()) {
        const {icon, label, iconHeight} = this.getIconAndLabel(result);
        result.customOptions = {
          label,
          icon,
          clickHandler: this.defaultClickHandler,
          type: 'feature',
          iconType: 'vue',
          style: index === numResults - 1 ? 'divider' : '',
        };

        if (iconHeight) {
          result.customOptions.iconHeight = iconHeight;
        }
      }

      this.searchIndices.collection.results = output;
      this.loading = false;
    },
    /**
     * Sets the search term to a new value. Should be called when the input is changed.
     * @param {string} newSearchTerm - The new term.
     */
    updateSearchTerm(newSearchTerm) {
      this.searchTerm = newSearchTerm;
      this.loading = true;
      this.renderCollectionResults('features');
    },
    /**
     * This default click handler is used to handle clicking on a search result in the SearchResultsList.
     * @param {Object} result - The selected result from the SearchResultsList to handle
     */
    async defaultClickHandler(result) {
      try {
        // This click handler assumes that the result item is a feature
        const feature = result.item;
        // Pan and zoom to the feature and then display the location
        feature.panAndZoom();
        (await feature.graphic).displayLocation();
      } catch (error) {
        error.message = `Failed to get graphic from search result: ${error.message}`;
        console.error(error);
      }
      this.shouldCollapseMenu = true;
    },
    getIconAndLabel(result) {
      /**
       * @param {Array<[string, FeatureOption]>} arr
       * @param {Record<string, FeatureOption>} curr An object storing the key-value pairs.
       * @return {Record<string, FeatureOption>}
       */
      function getKeysAndValues(arr, curr = {}) {
        arr.forEach(([key, value]) => {
          if (value?.options) {
            return getKeysAndValues(Array.from(value.options.entries()), curr);
          } else {
            curr[key] = value;
          }
        });
        return curr;
      }

      const allFeatureOptions = getKeysAndValues(Array.from(featureOptions.entries()));
      const option = allFeatureOptions[result.item.featureType];
      const icon = option?.icon?.raw;
      const iconHeight = option?.icon?.height;
      const label = option?.makeLabel(result);
      return {icon, label, iconHeight};
    },
  },
};
</script>
